import {
  Alert, Box, Button, Card, CardContent, Chip, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { CloudUpload, Download, ArrowBack } from '@mui/icons-material';
import { useState, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Papa from 'papaparse';
import { useNavigate } from 'react-router-dom';
import { api, extractError } from '../../../shared/api';
import type { Class } from '../../../shared/types';
import PageHeader from '../../../shared/PageHeader';

interface BulkResult {
  results: { row: number; ok: boolean; message: string; defaultPassword?: string }[];
}

const TEMPLATE_HEADERS = ['rollNumber', 'firstName', 'lastName', 'dob', 'gender', 'classId', 'parentName', 'parentPhone', 'parentEmail', 'bloodGroup'];

export default function StudentBulkUploadPage() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const navigate = useNavigate();

  const classesQ = useQuery({ queryKey: ['classes'], queryFn: async () => (await api.get<Class[]>('/classes')).data });
  const upload = useMutation({
    mutationFn: async () => (await api.post<BulkResult>('/admin/students/bulk', { rows })).data,
  });

  const onFile = (file: File) => {
    Papa.parse<any>(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        setParseErrors(res.errors.map((e) => `Row ${e.row}: ${e.message}`));
        setRows(res.data);
      },
    });
  };

  const downloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(',') + '\nR900,Sample,Student,2012-04-15,MALE,cls_5_a,Parent Name,+919876543210,parent@x.com,O+\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'student-bulk-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <PageHeader
        title="Bulk Student Upload"
        subtitle="Upload a CSV to enroll many students at once. Default passwords will be auto-generated."
        action={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/portal/students')}>Back</Button>
            <Button variant="outlined" startIcon={<Download />} onClick={downloadTemplate}>Template</Button>
          </Stack>
        }
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Available class IDs</Typography>
          <Box sx={{ mt: 1 }}>
            {(classesQ.data ?? []).map((c) => (
              <Chip key={c.id} label={`${c.name}-${c.section} → ${c.id}`} sx={{ mr: 1, mb: 1, fontFamily: 'monospace' }} />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ sm: 'row' }} alignItems="center" spacing={2}>
            <input
              ref={fileRef} type="file" accept=".csv"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
            />
            <Button variant="contained" startIcon={<CloudUpload />} onClick={() => fileRef.current?.click()}>
              Choose CSV file
            </Button>
            <Typography color="text.secondary">{rows.length ? `${rows.length} rows parsed` : 'No file selected'}</Typography>
            {rows.length > 0 && (
              <Button variant="contained" color="secondary" onClick={() => upload.mutate()} disabled={upload.isPending}>
                {upload.isPending ? 'Uploading…' : `Upload ${rows.length} students`}
              </Button>
            )}
          </Stack>
          {parseErrors.length > 0 && <Alert severity="warning" sx={{ mt: 2 }}>{parseErrors.join('; ')}</Alert>}
          {upload.isError && <Alert severity="error" sx={{ mt: 2 }}>{extractError(upload.error)}</Alert>}
        </CardContent>
      </Card>

      {rows.length > 0 && !upload.data && (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ p: 2, fontWeight: 700 }}>Preview ({rows.length} rows)</Typography>
          <Table size="small">
            <TableHead><TableRow>{Object.keys(rows[0]).map((k) => <TableCell key={k}>{k}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {rows.slice(0, 50).map((r, i) => (
                <TableRow key={i}>{Object.values(r).map((v: any, j) => <TableCell key={j}>{String(v)}</TableCell>)}</TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {upload.data && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Upload result</Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Chip color="success" label={`${upload.data.results.filter((r) => r.ok).length} succeeded`} />
              <Chip color="error" label={`${upload.data.results.filter((r) => !r.ok).length} failed`} />
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Row</TableCell><TableCell>Status</TableCell><TableCell>Message</TableCell><TableCell>Default password</TableCell></TableRow></TableHead>
                <TableBody>
                  {upload.data.results.map((r) => (
                    <TableRow key={r.row}>
                      <TableCell>{r.row}</TableCell>
                      <TableCell>
                        <Chip size="small" color={r.ok ? 'success' : 'error'} label={r.ok ? 'OK' : 'Failed'} />
                      </TableCell>
                      <TableCell>{r.message}</TableCell>
                      <TableCell>
                        {r.defaultPassword && (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#F1F5F9', px: 1, py: 0.5, borderRadius: 1 }}>
                            {r.defaultPassword}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
